import { useEffect, useState } from "react";
import { theme, btnSecondary } from "../../theme";
import { api, startRoleSwitch, setViewingOrgId } from "../../api/client";
import { useCurrentUser } from "../../auth/useCurrentUser";

/** One member of the client organisation, as GET /consultant/users returns them. */
export interface PersonaCandidate {
  id: string;
  displayName: string;
  isActive: boolean;
  organizationId: string | null;
  jobTitle?: string | null;
  clientRole?: string | null;
}

/**
 * Opens a session as one of a demo organisation's people (POST /auth/view-as-persona).
 * The server decides — demo organisations only (any for a platform administrator, the
 * consultant's own firm's set otherwise), every start audited in that organisation's log
 * (lib/demoPersona.ts); a page only offers it.
 */
export async function viewAsPersona(userId: string): Promise<void> {
  await startRoleSwitch<{ accessToken: string }>("/auth/view-as-persona", { userId });
  setViewingOrgId(null);
  // A fresh load so the whole app resolves the persona's identity from /auth/me.
  window.location.assign("/actions");
}

const roleLabel = (role: string | null | undefined) =>
  role ? role.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()) : "";

/** "Name — job title", or the built-in role when no title is held. */
export function personaLabel(u: PersonaCandidate): string {
  const what = u.jobTitle?.trim() || roleLabel(u.clientRole);
  return what ? `${u.displayName} — ${what}` : u.displayName;
}

/**
 * The header's "View as" picker for a demo organisation: every active member, so a
 * demonstration can show the platform exactly as a CISO, a DPO or a viewer sees it.
 * Renders nothing unless the organisation is a demo. A consultant who can open a demo
 * organisation's client view may see it, so the server's answer is what decides.
 */
export function ViewAsPersonaPicker({ orgId, isDemo }: { orgId: string; isDemo: boolean }) {
  const { user: me } = useCurrentUser();
  const allowed = isDemo && me?.role === "consultant" && !me?.viewedBy;
  const [people, setPeople] = useState<PersonaCandidate[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!allowed) return;
    api.get<{ users: PersonaCandidate[] }>("/consultant/users")
      .then((d) => setPeople(d.users.filter((u) => u.organizationId === orgId && u.isActive)))
      .catch(() => setError("Could not load this organisation's people"));
  }, [allowed, orgId]);

  if (!allowed) return null;

  async function choose(userId: string) {
    if (!userId) return;
    setBusy(true); setError("");
    try {
      await viewAsPersona(userId);
    } catch (e) {
      setBusy(false);
      setError(e instanceof Error && e.message ? e.message : "Could not open the view");
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, marginTop: 2 }}>
      <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, color: theme.color.subtle, whiteSpace: "nowrap" }}>
        View as
        <select
          aria-label="View the platform as one of this organisation's people"
          title="See the platform exactly as this person does: their sidebar, their permissions. Changes are saved as theirs."
          value=""
          disabled={busy || people.length === 0}
          onChange={(e) => void choose(e.target.value)}
          style={{ ...btnSecondary, padding: "5px 8px", fontSize: 11, maxWidth: 260, cursor: busy ? "wait" : "pointer" }}
        >
          <option value="">{busy ? "Opening…" : people.length ? "Choose a person…" : "No active members"}</option>
          {people.map((u) => <option key={u.id} value={u.id}>{personaLabel(u)}</option>)}
        </select>
      </label>
      {error && <div role="alert" style={{ fontSize: 11, color: theme.color.dangerText }}>{error}</div>}
    </div>
  );
}
